// Custom hook for URL-Filter state synchronization
import { useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import { useSearch } from '../../../shared/contexts/SearchContext';
import { 
  parseUrlFilters, 
  generateUrlParams, 
  validateTagFilters, 
  validateAppFilters 
} from '../utils/urlFilters';
import type { GlobalSearchFilters } from '../components/GlobalSearch';
import type { Id } from '../../../../convex/_generated/dataModel';

/**
 * Custom hook that manages bidirectional synchronization between URL parameters and filter state
 * 
 * Note: With the enhanced SearchContext, URL synchronization is now built into the context.
 * This hook maintains backward compatibility but delegates most work to the context.
 */
export function useTagUrlSync() {
  const location = useLocation();
  const navigate = useNavigate();
  const { 
    filters, 
    setFilters, 
    filterState, 
    filterDispatch,
    syncFromLegacyFilters,
    syncToLegacyFilters 
  } = useSearch();
  
  // Fetch available tags and apps for validation
  const allTags = useQuery(api.tags.getAllUserTags) || [];
  const allApps = useQuery(api.apps.listApps) || [];

  // Update URL when legacy filter state changes
  const updateUrlFromFilters = useCallback((newFilters: GlobalSearchFilters) => {
    // Use the new filter system's URL generation instead
    syncFromLegacyFilters(newFilters);
    
    // The SearchContext now handles URL updates automatically
    // But we keep this for any legacy components that might need it
    const params = generateUrlParams({
      tags: newFilters.tags,
      apps: newFilters.apps,
      searchText: newFilters.searchText
    });
    
    const queryString = params.toString();
    const newUrl = `${location.pathname}${queryString ? `?${queryString}` : ''}`;
    
    // Only navigate if the URL actually changes
    const currentUrl = `${location.pathname}${location.search}`;
    if (newUrl !== currentUrl) {
      // Let the SearchContext handle the URL update instead
      // navigate(newUrl, { replace: true });
    }
  }, [location.pathname, location.search, syncFromLegacyFilters]);

  // Update legacy filter state from URL when needed
  const updateFiltersFromUrl = useCallback(() => {
    // The SearchContext now handles URL parsing automatically
    // This is kept for backward compatibility
    const searchParams = new URLSearchParams(location.search);
    const urlFilters = parseUrlFilters(searchParams);
    
    // Check if we need to convert from legacy URL format
    if (urlFilters.tags.length > 0 || urlFilters.apps.length > 0 || urlFilters.search) {
      // Validate and convert URL parameters to filter format
      const validatedTags = validateTagFilters(urlFilters.tags, allTags).map(tag => ({
        id: tag.id as Id<'tags'>,
        name: tag.name,
        negative: tag.negative
      }));
      
      const validatedApps = validateAppFilters(urlFilters.apps, allApps).map(app => ({
        id: app.id as Id<'apps'>,
        name: app.name,
        negative: app.negative
      }));

      // Create new filter state from URL
      const newFilters: GlobalSearchFilters = {
        searchText: urlFilters.search || '',
        tags: validatedTags,
        apps: validatedApps,
        rawQuery: buildRawQuery(validatedTags, validatedApps, urlFilters.search)
      };

      // Only update if filters actually changed
      if (!areFiltersEqual(filters, newFilters)) {
        setFilters(newFilters);
        // Also sync to new filter system
        syncFromLegacyFilters(newFilters);
      }
    }
  }, [location.search, allTags, allApps, filters, setFilters, syncFromLegacyFilters]);

  // Initialize filters from URL on mount and when dependencies change
  // The SearchContext now handles this, but we keep it for legacy support
  useEffect(() => {
    if (allTags.length > 0 || allApps.length > 0) {
      // Only do legacy URL parsing if the new system hasn't initialized from URL yet
      const searchParams = new URLSearchParams(location.search);
      const hasNewFormatParams = searchParams.has('tag') || searchParams.get('q');
      
      if (!hasNewFormatParams) {
        updateFiltersFromUrl();
      }
    }
  }, [location.search, allTags.length, allApps.length]); // Only trigger when dependencies are available

  // Sync legacy filters to new filter system when they change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      // Convert legacy filters to new format and dispatch
      syncFromLegacyFilters(filters);
    }, 100); // Small delay to prevent excessive updates

    return () => clearTimeout(timeoutId);
  }, [filters, syncFromLegacyFilters]);

  return {
    updateUrlFromFilters,
    updateFiltersFromUrl,
    // Expose new filter system for advanced usage
    newFilterState: filterState,
    newFilterDispatch: filterDispatch
  };
}

/**
 * Build raw query string from filter components
 */
function buildRawQuery(
  tags: Array<{ name: string; negative?: boolean }>,
  apps: Array<{ name: string; negative?: boolean }>,
  searchText?: string
): string {
  const parts: string[] = [];

  // Add search text
  if (searchText?.trim()) {
    parts.push(searchText.trim());
  }

  // Add tag filters
  tags.forEach(tag => {
    parts.push(`${tag.negative ? '-' : ''}tag:${tag.name}`);
  });

  // Add app filters
  apps.forEach(app => {
    parts.push(`${app.negative ? '-' : ''}app:${app.name}`);
  });

  return parts.join(' ');
}

/**
 * Compare two filter objects for equality
 */
function areFiltersEqual(a: GlobalSearchFilters, b: GlobalSearchFilters): boolean {
  if (a.searchText !== b.searchText) return false;
  if (a.tags.length !== b.tags.length) return false;
  if (a.apps.length !== b.apps.length) return false;

  // Compare tags
  for (let i = 0; i < a.tags.length; i++) {
    const tagA = a.tags[i];
    const tagB = b.tags[i];
    if (tagA.id !== tagB.id || tagA.name !== tagB.name || tagA.negative !== tagB.negative) {
      return false;
    }
  }

  // Compare apps
  for (let i = 0; i < a.apps.length; i++) {
    const appA = a.apps[i];
    const appB = b.apps[i];
    if (appA.id !== appB.id || appA.name !== appB.name || appA.negative !== appB.negative) {
      return false;
    }
  }

  return true;
}
